// OverflowTooltip.jsx
import React, { useRef } from "react";
import styled from "styled-components";
import { isNoWrapString } from "../utility/functions";
import { useHoverTooltip } from "../HoverTooltip";

const Wrapper = styled.div`
    position: relative;
    display: inline-block;
    width: 100%;
`;

const TooltipEventAnchor = styled.span`
    display: inherit;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
`;


const getTextContent = (children) => {
    const text = React.Children.toArray(children)
    .map((child) => {
        if (typeof child === "string" || typeof child === "number") {
            return String(child);
        }

        if (child?.props?.children) {
            return getTextContent(child.props.children) ?? "";
        }

        return "";
    })
    .join(" ");

    return text === "" ? null : text;
};



const OverflowTooltip = ({ tooltip, children }) => {
    const wrapperRef = useRef(null); //used to scale tt width relative to cell width
    const ttRef = useRef(null); //used for mouse events and actual content size attributes
    const { showTooltip, hideTooltip } = useHoverTooltip();

    const text = getTextContent(tooltip ?? children);
    const noWrap = isNoWrapString(text);

    const handleMove = (e) => {
        const wrapper = wrapperRef.current;
        const content = ttRef.current;
        if (!wrapper || !content) return;

        //supress trying to show tooltip if text is part of button
        const isButton = (el) =>
            el?.closest?.('button, [role="button"]') ||
            el?.querySelector?.('button, [role="button"]');

        if (isButton(e.target) || isButton(content)) {
            hideTooltip(content, { force: true });
            return;
        }

        const isOverflowing = content.scrollWidth > content.clientWidth;
        const shouldShow = tooltip || (isOverflowing && text) ;
        
        if (!shouldShow) {
            hideTooltip(content);
            return;
        }

        const parentWidth = wrapper.clientWidth;

        const baseWidth = content.scrollWidth;

        const maxWidth = noWrap ? baseWidth : parentWidth * 1.3;

        showTooltip({
            text,
            x: e.clientX,
            y: e.clientY,
            wrap: noWrap ? 'nowrap' : null,
            maxWidth,
            trigger: content
        });
    };

    return (
        <Wrapper ref={wrapperRef}>
            <TooltipEventAnchor
                ref={ttRef}
                onMouseMove={handleMove}
                onMouseLeave={() => hideTooltip(ttRef.current)}
                onMouseDown={() => hideTooltip(ttRef.current)}
            >
                {children}
            </TooltipEventAnchor>
        </Wrapper>
    );
};

export default OverflowTooltip;
